import React, { Component } from "react";
import { Row, Col } from "react-flexbox-grid";
import {
  BarChart,
  Card,
  HorizontalBarChart,
  PieChart,
  TabsCustom,
  TabsCustomMenu,
  TabsCustomHeader,
  TabsCustomBody,
  TabsCustomItem,
} from "ui-kit-ck-consultant";

import MonthBarChart from "./MonthBarChart";

import {
  getCurrencyValue,
  getPercentageValue,
  getValue,
} from "../../../utils/general";
import { getColors } from "../../../utils/colors";

import AuthContext from "../../../context/AuthContext";

export default class Overall extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tabsCAId: 0,
      tabsCA: ["Région", "Agence"],
      tabsCompanyId: 0,
      tabsCompany: ["Volume", "CA"],
    };
  }

  static contextType = AuthContext;

  render() {
    // Volume de missions reçues : comparaison au même mois de N-1 — barres =
    // écart en nombre de missions, ligne = écart en %. Les mois futurs sont
    // masqués (aucune mission encore reçue, l'écart serait trompeur).
    const missionMonth = this.props.data.month.yield.mission || [];
    // hasMissionN1 false = payload issued by an older backend (cache) : on
    // n'affiche rien plutôt que des barres trompeuses (volume brut sans N-1).
    const hasMissionN1 = Array.isArray(this.props.data.month.missionN1);
    const missionN1 = this.props.data.month.missionN1 || [];
    const monthLabels = this.props.data.month.labels || [];
    const now = new Date();
    const currentMonthLabel = `${now.getFullYear()}-${String(
      now.getMonth() + 1
    ).padStart(2, "0")}`;
    const missionDelta = missionMonth.map((value, index) =>
      !hasMissionN1 || monthLabels[index] > currentMonthLabel
        ? null
        : (value || 0) - (missionN1[index] || 0)
    );
    const missionVariation = missionMonth.map((value, index) => {
      if (monthLabels[index] > currentMonthLabel || !missionN1[index]) {
        return null;
      }
      const pct =
        Math.round(
          (((value || 0) - missionN1[index]) * 10000) / missionN1[index]
        ) / 100;
      // Base N-1 non comparable (année d'onboarding : périmètre entré en cours
      // d'année N-1) : un % à 4 chiffres n'a aucun sens métier, on ne trace
      // pas le point — les barres conservent l'écart en nombre de missions.
      return missionN1[index] < 10 || Math.abs(pct) > 300 ? null : pct;
    });

    // Échelles symétriques autour de 0 pour les deux axes : le zéro de la
    // ligne (%) et celui des barres (nombre) partagent la même ligne centrale,
    // sinon les deux séries se croisent de façon illisible.
    const maxAbsVariation = Math.max(
      ...missionVariation.filter((value) => value !== null).map(Math.abs),
      1
    );
    const maxAbsDelta = Math.max(
      ...missionDelta.filter((value) => value !== null).map(Math.abs),
      1
    );

    // Cumul à date (période plafonnée à aujourd'hui vs même période N-1),
    // affiché à côté du titre de la card.
    const missionComparison = this.props.data.year.missionComparison || {};
    let missionTitle = "Volume missions reçues - évolution vs N-1";
    if (missionComparison.previous || missionComparison.current) {
      missionTitle += ` — à date : ${getValue(
        missionComparison.current || 0,
        0
      )} vs ${getValue(missionComparison.previous || 0, 0)} en N-1`;
      if (
        missionComparison.percentage !== null &&
        missionComparison.percentage !== undefined
      ) {
        missionTitle += ` (${
          missionComparison.percentage > 0 ? "+" : ""
        }${getPercentageValue(missionComparison.percentage)})`;
      }
    }

    // Même lecture pour le CA : barres = écart en euros vs le même mois de
    // N-1, ligne = écart en %. Mois futurs masqués.
    const CAMonth = this.props.data.month.CA.total || [];
    // hasCAN1 false = payload d'un backend antérieur (cache Redis 5 min) : on
    // n'affiche rien plutôt qu'un CA brut présenté comme un écart.
    const hasCAN1 = Array.isArray(this.props.data.month.CAN1);
    const CAN1 = this.props.data.month.CAN1 || [];
    const CADelta = CAMonth.map((value, index) =>
      !hasCAN1 || monthLabels[index] > currentMonthLabel
        ? null
        : Math.round(((value || 0) - (CAN1[index] || 0)) * 100) / 100
    );
    const CAVariation = CAMonth.map((value, index) => {
      if (monthLabels[index] > currentMonthLabel || !CAN1[index]) {
        return null;
      }
      const pct =
        Math.round((((value || 0) - CAN1[index]) * 10000) / CAN1[index]) / 100;
      // Base N-1 non comparable (périmètre entré en cours d'année N-1, ou
      // quelques honoraires isolés) : on ne trace pas le point, les barres
      // conservent l'écart en euros.
      return CAN1[index] < 1000 || Math.abs(pct) > 300 ? null : pct;
    });

    const maxAbsCAVariation = Math.max(
      ...CAVariation.filter((value) => value !== null).map(Math.abs),
      1
    );
    const maxAbsCADelta = Math.max(
      ...CADelta.filter((value) => value !== null).map(Math.abs),
      1
    );

    // Cumul à date : même période plafonnée à aujourd'hui vs N-1. `current`
    // reprend exactement le CA cumulé affiché au-dessus du disque de CA.
    const CAComparison = this.props.data.year.CAComparison || {};
    let CATitle = "CA - évolution vs N-1";
    if (CAComparison.previous || CAComparison.current) {
      CATitle += ` — à date : ${getCurrencyValue(
        CAComparison.current || 0
      )} vs ${getCurrencyValue(CAComparison.previous || 0)} en N-1`;
      if (
        CAComparison.percentage !== null &&
        CAComparison.percentage !== undefined
      ) {
        CATitle += ` (${
          CAComparison.percentage > 0 ? "+" : ""
        }${getPercentageValue(CAComparison.percentage)})`;
      }
    }

    return (
      <Row>
        <Col xs={12} md={3} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Taux d'EAD : ${getPercentageValue(
              this.props.data.year.EAD
            )}`}
            format="percentage"
            label="Taux d'EAD"
            data={this.props.data.month.EAD}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.EAD_cumul}
          />
        </Col>
        <Col xs={12} md={3} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Taux de RIV/VEI : ${getPercentageValue(
              this.props.data.year.RIV_VEI
            )}`}
            format="percentage"
            label="Taux de RIV/VEI"
            data={this.props.data.month.RIV_VEI}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.RIV_VEI_cumul}
          />
        </Col>
        <Col xs={12} md={3} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Taux de PRE : ${getPercentageValue(
              this.props.data.year.PRE
            )}`}
            format="percentage"
            label="Taux de PRE"
            data={this.props.data.month.PRE}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.PRE_cumul}
          />
        </Col>
        <Col xs={12} md={3} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Taux de RP : ${getPercentageValue(
              this.props.data.year.RP
            )}`}
            format="percentage"
            label="Taux de RP"
            data={this.props.data.month.RP}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.RP_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Taux de réparation pièces cibles : ${getPercentageValue(
              this.props.data.year.targetPieces
            )}`}
            format="percentage"
            label="Taux de réparation pièces cibles"
            data={this.props.data.month.targetPieces}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.targetPieces_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Coût moyen : ${getCurrencyValue(
              this.props.data.year.totalReport
            )} HT`}
            format="currency"
            label="Coût moyen"
            data={this.props.data.month.totalReport}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.totalReport_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`VRADE moyenne : ${getCurrencyValue(
              this.props.data.year.VRADE
            )} HT`}
            format="currency"
            label="VRADE moyenne"
            data={this.props.data.month.VRADE}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.VRADE_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Délai moyen réparable: ${getValue(
              this.props.data.year.delayR
            )} jours`}
            label="Délai moyen réparable"
            data={this.props.data.month.delayR}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.delayR_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Délai moyen non-réparable: ${getValue(
              this.props.data.year.delayNR
            )} jours`}
            labe="Délai moyen non-réparable"
            data={this.props.data.month.delayNR}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.delayNR_cumul}
          />
        </Col>
        <Col xs={12} md={4} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title={`Délai moyen SD29: ${getValue(
              this.props.data.year.delaySD29
            )} jours`}
            label="Délai moyen SD29"
            data={this.props.data.month.delaySD29}
            labels={this.props.data.month.labels}
            dataCumulativeAverage={this.props.data.month.delaySD29_cumul}
          />
        </Col>
        <Col xs={12} md={6} className={"mb-10 mb-20-xl"}>
          <MonthBarChart
            title="Évolution stock dossiers"
            type="bar"
            label="stock dossiers"
            labelLine="Âge moyen des dossiers (jours)"
            digits={0}
            data={this.props.data.month.stock}
            dataLine={this.props.data.month.ageStock}
            labels={this.props.data.month.labels}
          />
        </Col>
        <Col xs={12} md={6} className={"mb-10 mb-20-xl"}>
          <Card title="CA mois / mois">
            <BarChart
              format="currency"
              labels={this.props.data.month.labels}
              widthBar={30}
              dataSet={[
                {
                  label: "CA",
                  data: this.props.data.month.CA.total,
                  color: "#026fc2",
                },
                {
                  label: "Atteinte",
                  data: this.props.data.month.CA.total.map(
                    (element, idx) =>
                      element - this.props.data.month.CA.goal[idx]
                  ),
                  color: this.props.data.month.CA.total.map((element, idx) =>
                    element - this.props.data.month.CA.goal[idx] > 0
                      ? "#238037"
                      : "#ea0000"
                  ),
                },
                {
                  label: "Objectif",
                  type: "line",
                  data: this.props.data.month.CA.goal,
                  color: "#026fc2",
                },
              ]}
              xAxes={[
                {
                  stacked: true,
                },
              ]}
              yAxes={[
                {
                  stacked: true,
                  ticks: {
                    display: false,
                  },
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} md={12} className={"mb-10 mb-20-xl"}>
          <Card
            title={`Rendement mois / mois -  (${this.props.data.year.totalMission} / ${this.props.data.year.totalClosing})`}
          >
            <BarChart
              type="bar"
              labels={this.props.data.month.labels}
              digits={0}
              widthBar={30}
              datalabels2={[
                {
                  display: true,
                  align: "bottom",
                  anchor: "start",
                  clamp: true,
                  color: "#0162AD",
                  backgroundColor: "white",
                  borderColor: "#0162AD",
                  borderWidth: 1,
                  borderRadius: 4,
                  font: {
                    size: "10",
                  },
                  formatter: (value) => getPercentageValue(value),
                },
              ]}
              dataSet={[
                {
                  label: "Rendement (en %)",
                  type: "line",
                  data: this.props.data.month.yield.yield,
                  color: "#026fc2",
                  yAxisID: "A",
                },
                {
                  label: "Dossiers déposés",
                  data: this.props.data.month.yield.closing,
                  type: "bar",
                  color: "#51AE50",
                  yAxisID: "B",
                  xAxisID: "xB",
                },
                {
                  label: "Dossiers reçus",
                  data: this.props.data.month.yield.mission,
                  type: "bar",
                  color: "#E68A00",
                  yAxisID: "B",
                  xAxisID: "xA",
                },
              ]}
              xAxes={[
                {
                  id: "xA",
                  stacked: true,
                  display: true,
                  barThickness: 45,
                },
                {
                  id: "xB",
                  display: false,
                  stacked: true,
                  barThickness: 30,
                  type: "category",
                  categoryPercentage: 0.6,
                  barPercentage: 0.9,
                  gridLines: {
                    display: false,
                    offsetGridLines: true,
                  },
                  offset: true,
                },
              ]}
              yAxes={[
                {
                  id: "A",
                  display: false,
                  ticks: {
                    beginAtZero: true,
                  },
                },
                {
                  id: "B",
                  display: false,
                  ticks: {
                    beginAtZero: true,
                  },
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} className={"mb-10 mb-20-xl"}>
          <Card title={missionTitle}>
            <BarChart
              labels={this.props.data.month.labels}
              digits={0}
              widthBar={30}
              datalabels2={[
                {
                  display: true,
                  align: "top",
                  anchor: "end",
                  clamp: true,
                  color: "#0162AD",
                  backgroundColor: "white",
                  borderColor: "#0162AD",
                  borderWidth: 1,
                  borderRadius: 4,
                  font: {
                    size: "10",
                  },
                  formatter: (value) =>
                    value === null
                      ? ""
                      : (value > 0 ? "+" : "") + getPercentageValue(value),
                },
              ]}
              dataSet={[
                {
                  label: "Évolution vs mois N-1 (en %)",
                  type: "line",
                  data: missionVariation,
                  color: "#026fc2",
                  yAxisID: "A",
                },
                {
                  label: "Missions en +/- vs mois N-1",
                  type: "bar",
                  data: missionDelta,
                  color: missionDelta.map((value) =>
                    value !== null && value < 0 ? "#ea0000" : "#238037"
                  ),
                  yAxisID: "B",
                },
              ]}
              yAxes={[
                {
                  id: "A",
                  display: false,
                  ticks: {
                    min: -maxAbsVariation * 1.2,
                    max: maxAbsVariation * 1.2,
                  },
                },
                {
                  id: "B",
                  ticks: {
                    display: false,
                    min: -maxAbsDelta * 1.2,
                    max: maxAbsDelta * 1.2,
                  },
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} md={6} className={"mb-10 mb-20-xl"}>
          <Card title={CATitle}>
            <BarChart
              labels={this.props.data.month.labels}
              widthBar={30}
              datalabels2={[
                {
                  display: true,
                  align: "top",
                  anchor: "end",
                  clamp: true,
                  color: "#0162AD",
                  backgroundColor: "white",
                  borderColor: "#0162AD",
                  borderWidth: 1,
                  borderRadius: 4,
                  font: {
                    size: "10",
                  },
                  formatter: (value) =>
                    value === null
                      ? ""
                      : (value > 0 ? "+" : "") + getPercentageValue(value),
                },
              ]}
              dataSet={[
                {
                  label: "Évolution vs mois N-1 (en %)",
                  type: "line",
                  data: CAVariation,
                  color: "#026fc2",
                  yAxisID: "A",
                },
                {
                  label: "CA en +/- vs mois N-1 (€ HT)",
                  type: "bar",
                  data: CADelta,
                  color: CADelta.map((value) =>
                    value !== null && value < 0 ? "#ea0000" : "#238037"
                  ),
                  yAxisID: "B",
                },
              ]}
              yAxes={[
                {
                  id: "A",
                  display: false,
                  ticks: {
                    min: -maxAbsCAVariation * 1.2,
                    max: maxAbsCAVariation * 1.2,
                  },
                },
                {
                  id: "B",
                  ticks: {
                    display: false,
                    min: -maxAbsCADelta * 1.2,
                    max: maxAbsCADelta * 1.2,
                  },
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} className={"mb-10 mb-20-xl"}>
          <Card
            title={`${this.props.year} - CA cumulé : ${getCurrencyValue(
              this.props.data.year.CA.total
            )} /  ${getCurrencyValue(this.props.data.year.CA.goal)}`}
          >
            <TabsCustom>
              <TabsCustomHeader noCard>
                {this.state.tabsCA.map((element, key) => (
                  <TabsCustomMenu
                    noCard
                    key={key}
                    title={element}
                    active={this.state.tabsCAId === key}
                    onClick={(e) => {
                      this.setState({ tabsCAId: key });
                    }}
                  />
                ))}
              </TabsCustomHeader>
              <TabsCustomBody noCard>
                <TabsCustomItem active={this.state.tabsCAId === 0}>
                  <PieChart
                    height={380}
                    full
                    type="pie"
                    legendDisplay={true}
                    positionLegend="right"
                    format="currency"
                    data={this.props.data.year.CARegion.map((region, idx) => ({
                      labels: region.name
                        ? `${region.name} (${getPercentageValue(region.percentage)})`
                        : `Autre (${getPercentageValue(region.percentage)})`,
                      data: region.data,
                      color: getColors()[idx],
                    }))}
                  />
                </TabsCustomItem>
                <TabsCustomItem active={this.state.tabsCAId === 1}>
                  <PieChart
                    height={380}
                    full
                    type="pie"
                    legendDisplay={true}
                    positionLegend="right"
                    format="currency"
                    data={this.props.data.year.CAAgency.map((agency) => ({
                      labels: agency.name
                        ? `${agency.name} (${getPercentageValue(agency.percentage)})`
                        : `Autre (${getPercentageValue(agency.percentage)})`,
                      data: agency.data,
                      color: agency.color,
                    }))}
                  />
                </TabsCustomItem>
              </TabsCustomBody>
            </TabsCustom>
          </Card>
        </Col>
        <Col xs={12} md={12} lg={4} className={"mb-10 mb-20-xl"}>
          <Card title={`${this.props.year} - Dossiers en retard / cabinet`}>
            <HorizontalBarChart
              height={260}
              digits={0}
              format="percentage"
              align
              isLegend={true}
              xAxes={[
                {
                  ticks: {
                    display: false,
                    suggestedMax: 10,
                    min: 0,
                    beginAtZero: true,
                  },
                  offset: false,
                  scaleLabel: {
                    fontSize: 8,
                  },
                },
              ]}
              yAxes={[
                {
                  ticks: {
                    display: false,
                    suggestedMax: 10,
                    min: 0,
                    beginAtZero: true,
                  },
                  offset: false,
                  scaleLabel: {
                    fontSize: 8,
                  },
                },
              ]}
              labels={this.props.data.year.overdueFolderAgency.map(
                (agency) => agency.name
              )}
              dataSet={[
                {
                  label: "30j - 60j",
                  data: this.props.data.year.overdueFolderAgency.map(
                    (agency) => agency.data[30]
                  ),
                  color: "#268540",
                },
                {
                  label: "60j - 120j",
                  data: this.props.data.year.overdueFolderAgency.map(
                    (agency) => agency.data[60]
                  ),
                  color: "#facc02",
                },
                {
                  label: "+ 120j",
                  data: this.props.data.year.overdueFolderAgency.map(
                    (agency) => agency.data[120]
                  ),
                  color: "#db2828",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} md={12} lg={4} className={"mb-10 mb-20-xl"}>
          <Card title={`${this.props.year} - Dossiers en retard / genre`}>
            <HorizontalBarChart
              height={260}
              digits={0}
              format="percentage"
              align
              isLegend={true}
              xAxes={[
                {
                  ticks: {
                    display: false,
                    suggestedMax: 10,
                    min: 0,
                    beginAtZero: true,
                  },
                  offset: false,
                  scaleLabel: {
                    fontSize: 8,
                  },
                },
              ]}
              yAxes={[
                {
                  ticks: {
                    display: false,
                    suggestedMax: 10,
                    min: 0,
                    beginAtZero: true,
                  },
                  offset: false,
                  scaleLabel: {
                    fontSize: 8,
                  },
                },
              ]}
              labels={this.props.data.year.overdueFolderKind.map(
                (kind) => kind.name
              )}
              dataSet={[
                {
                  label: "30j - 60j",
                  data: this.props.data.year.overdueFolderKind.map(
                    (kind) => kind.data[30]
                  ),
                  color: "#268540",
                },
                {
                  label: "60j - 120j",
                  data: this.props.data.year.overdueFolderKind.map(
                    (kind) => kind.data[60]
                  ),
                  color: "#facc02",
                },
                {
                  label: "+ 120j",
                  data: this.props.data.year.overdueFolderKind.map(
                    (kind) => kind.data[120]
                  ),
                  color: "#db2828",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} md={12} lg={4} className={"mb-10 mb-20-xl"}>
          <Card title={`${this.props.year} - Dossiers en retard`}>
            <PieChart
              height={260}
              full
              type="pie"
              digits={0}
              legendDisplay={true}
              data={[
                {
                  labels: "30j - 60j",
                  data: this.props.data.year.overdueFolder[30],
                  color: "#268540",
                },
                {
                  labels: "60j - 120j",
                  data: this.props.data.year.overdueFolder[60],
                  color: "#facc02",
                },
                {
                  labels: "+ 120j",
                  data: this.props.data.year.overdueFolder[120],
                  color: "#db2828",
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={12} className={"mb-10 mb-20-xl"}>
          <Card
            title={`${this.props.year} - Répartition compagnie - (${this.props.data.year.totalMission} / ${this.props.data.year.totalClosing})`}
          >
            <TabsCustom>
              <TabsCustomHeader noCard>
                {this.state.tabsCompany.map((element, key) => (
                  <TabsCustomMenu
                    noCard
                    key={key}
                    title={element}
                    active={this.state.tabsCompanyId === key}
                    onClick={() => this.setState({ tabsCompanyId: key })}
                  />
                ))}
              </TabsCustomHeader>
              <TabsCustomBody noCard>
                <TabsCustomItem active={this.state.tabsCompanyId === 0}>
                  <PieChart
                    height={260}
                    full
                    type="pie"
                    digits={0}
                    legendDisplay={true}
                    positionLegend="right"
                    data={this.props.data.year.folderCompany.map((company) => ({
                      labels: company.name
                        ? `${company.name} (${getPercentageValue(company.percentage)})`
                        : `Autre (${getPercentageValue(company.percentage)})`,
                      data: company.total,
                      color: company.color,
                    }))}
                  />
                </TabsCustomItem>
                <TabsCustomItem active={this.state.tabsCompanyId === 1}>
                  <PieChart
                    height={260}
                    full
                    type="pie"
                    legendDisplay={true}
                    positionLegend="right"
                    format="currency"
                    data={(this.props.data.year.CACompany || []).map(
                      (company) => ({
                        labels: company.name
                          ? `${company.name} (${getPercentageValue(company.percentage)})`
                          : `Autre (${getPercentageValue(company.percentage)})`,
                        data: company.data,
                        color: company.color,
                      })
                    )}
                  />
                </TabsCustomItem>
              </TabsCustomBody>
            </TabsCustom>
          </Card>
        </Col>
      </Row>
    );
  }
}
